import type { AppRoleName, MeResponse, PersonDto, PersonSearchResultDto } from '@process-hub/shared';
import { useEffect, useState } from 'react';
import { api } from '../api';
import { SortHeader, useSortedRows, type SortColumn } from '../components/SortableTable';
import { describeError } from '../errors';

/**
 * Admin: who holds which Process Hub role (D-036). Roles are app role assignments on the Entra
 * app registration; this screen reads and changes them there. Departments come from the
 * SG-WT-DPT-* groups and are shown read-only. A change applies within about a minute (D-038).
 * The Teams column is where people are connected to the Process Hub app (D-061): Connect installs
 * it for them and opens the chat their notifications arrive in.
 */

const ROLES: Array<{ role: AppRoleName; label: string; help: string }> = [
  { role: 'Admin', label: 'Admin', help: 'Everything, every department: people, Teams connections, owner reassignment, automation proposals.' },
  { role: 'ProcessOwner', label: 'Process owner', help: 'Interviews, edits, submits, publishes and re-certifies their own processes.' },
  { role: 'Approver', label: 'Approver', help: 'Decides approval stages in their department. Department heads need this.' },
  { role: 'Reader', label: 'Reader', help: 'Reads published processes in their department and can flag problems.' },
];

const TEAMS_HELP = 'Whether Process Hub can message this person in Teams. Connect installs the Process Hub app for them and opens the chat their approval requests and notices arrive in.';

const NO_GROUP_HELP = 'Not a member of any SG-WT-DPT security group. Roles other than Admin are scoped to a department, so this person will see no processes and cannot approve or own anything until IT adds them to their department group.';

/** Sortable columns (D-047): person, departments, and one yes/no column per role — holders first on the first click. */
const PEOPLE_COLUMNS: ReadonlyArray<SortColumn<PersonDto>> = [
  { key: 'person', get: (p) => p.name },
  { key: 'departments', get: (p) => (p.departments.length ? p.departments.join(', ') : null) },
  { key: 'teams', get: (p) => p.teams.connected, firstDir: 'desc' },
  ...ROLES.map((x): SortColumn<PersonDto> => ({ key: x.role, get: (p) => p.roles.includes(x.role), firstDir: 'desc' })),
];

/** A role holder who is in none of the six department groups (D-039). Admin is department-wide, so it is exempt. */
export function outsideDepartments(p: { roles: AppRoleName[]; departments: string[] }): boolean {
  return p.departments.length === 0 && p.roles.some((r) => r !== 'Admin');
}

export function PeopleCard({ me, onNotice }: { me: MeResponse; onNotice: (m: string) => void }) {
  const [people, setPeople] = useState<PersonDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PersonSearchResultDto[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const { sorted, sort, toggle: sortBy } = useSortedRows(people, PEOPLE_COLUMNS, { key: 'person', dir: 'asc' });

  const load = () =>
    api
      .listPeople()
      .then(setPeople)
      .catch((e: unknown) => setError(describeError(e)));
  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults(null);
      return;
    }
    const t = setTimeout(() => {
      api
        .searchPeople(query.trim())
        .then(setResults)
        .catch(() => setResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const change = async (oid: string, name: string, roles: AppRoleName[], departments: string[]) => {
    setBusy(oid);
    try {
      const saved = await api.setRoles(oid, roles);
      const noGroup = outsideDepartments({ roles, departments: saved.departments ?? departments });
      onNotice(
        roles.length
          ? `${name}: ${roles.join(', ')}. Takes effect within a minute.${noGroup ? ' They are not in any SG-WT-DPT group, so they will see nothing until IT adds them to their department group.' : ''}`
          : `${name}: all Process Hub roles removed. They can no longer sign in.`,
      );
      setQuery('');
      setResults(null);
      await load();
    } catch (e) {
      onNotice(describeError(e, 'The roles could not be changed.'));
    } finally {
      setBusy(null);
    }
  };

  const toggle = (p: PersonDto, role: AppRoleName) => {
    const next = p.roles.includes(role) ? p.roles.filter((r) => r !== role) : [...p.roles, role];
    if (next.length === 0 && !window.confirm(`Remove every role from ${p.name}? They will not be able to sign in.`)) return;
    void change(p.oid, p.name, next, p.departments);
  };

  /** Adding from the directory: warn first when the person is in no department group and the role needs one. */
  const add = (r: PersonSearchResultDto, role: AppRoleName) => {
    if (outsideDepartments({ roles: [role], departments: r.departments }) && !window.confirm(`${r.name} is not in any SG-WT-DPT security group. As ${role} they will see no processes until IT adds them to their department group. Add them anyway?`)) return;
    void change(r.oid, r.name, [role], r.departments);
  };

  const connectTeams = async (p: PersonDto) => {
    setBusy(p.oid);
    try {
      const r = await api.connectTeams(p.oid);
      onNotice(r.note);
      await load();
    } catch (e) {
      onNotice(describeError(e, `${p.name} could not be connected to Teams.`));
    } finally {
      setBusy(null);
    }
  };

  const connectEveryone = async () => {
    setBusy('everyone');
    try {
      const results = await api.connectAllTeams();
      const ok = results.filter((r) => r.connected).length;
      const failed = results.filter((r) => !r.connected).map((r) => `${r.name}: ${r.note}`);
      onNotice(results.length === 0 ? 'Everyone with a role is already connected to Teams.' : [`Connected ${ok} of ${results.length}.`, ...failed].join(' '));
      await load();
    } catch (e) {
      onNotice(describeError(e, 'People could not be connected to Teams.'));
    } finally {
      setBusy(null);
    }
  };

  const testTeams = async (p: PersonDto) => {
    setBusy(p.oid);
    try {
      const r = await api.testTeams(p.oid);
      onNotice(`${p.name}: ${r.note}`);
      await load();
    } catch (e) {
      onNotice(describeError(e, 'The test could not be sent.'));
    } finally {
      setBusy(null);
    }
  };

  const outside = people?.filter(outsideDepartments).length ?? 0;
  const unconnected = people?.filter((p) => !p.teams.connected && p.accountEnabled !== false).length ?? 0;

  return (
    <div className="card">
      <div className="card__header">
        <div className="card__title">People and roles</div>
        <div className="card__meta">
          {people ? `${people.length} with a role` : 'Loading.'}
          {outside > 0 && (
            <>
              {' · '}
              <span className="pill pill--warn" title={NO_GROUP_HELP}>
                {outside} not in a department group
              </span>
            </>
          )}
          {' '}· departments come from the SG-WT-DPT groups · changes apply within a minute
          {unconnected > 0 && (
            <button type="button" className="button button--secondary" style={{ marginLeft: 12 }} disabled={busy !== null} onClick={() => void connectEveryone()} title="Install Process Hub in Teams for everyone with a role who is not connected yet">
              {busy === 'everyone' ? 'Connecting…' : `Connect everyone to Teams (${unconnected})`}
            </button>
          )}
        </div>
      </div>
      <div style={{ padding: '0 18px 12px' }}>
        {error && <div className="notice notice--error" style={{ marginTop: 12 }}>{error}</div>}
        <div className="field" style={{ marginTop: 12 }}>
          <input aria-label="Find a person to add" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Add a person: start typing a name or email" style={{ width: 360 }} />
        </div>
        {results && (
          <div className="card" style={{ margin: '0 0 12px' }}>
            {results.length === 0 && <div className="muted" style={{ padding: 12 }}>Nobody in the directory matches.</div>}
            {results.map((r) => (
              <div key={r.oid} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>
                <div style={{ flex: 1 }}>
                  <strong>{r.name}</strong> <span className="muted">· {r.upn}</span>
                  <div className="muted" style={{ fontSize: 12 }}>
                    {r.jobTitle ?? 'no title'} ·{' '}
                    {r.departments.length ? (
                      r.departments.join(', ')
                    ) : (
                      <span className="pill pill--warn" title={NO_GROUP_HELP}>
                        Not in a department group
                      </span>
                    )}
                    {!r.accountEnabled && ' · account disabled'}
                  </div>
                </div>
                {ROLES.map((x) => (
                  <button key={x.role} type="button" className="button button--secondary" title={x.help} disabled={busy !== null || !r.accountEnabled} onClick={() => add(r, x.role)}>
                    Add as {x.label}
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}
        {people && people.length > 0 && (
          <table className="grid">
            <thead>
              <tr>
                <SortHeader label="Person" sortKey="person" sort={sort} onToggle={sortBy} />
                <SortHeader label="Departments" sortKey="departments" sort={sort} onToggle={sortBy} />
                {ROLES.map((x) => (
                  <SortHeader key={x.role} label={x.label} sortKey={x.role} sort={sort} onToggle={sortBy} title={x.help} align="center" />
                ))}
                <SortHeader label="Teams" sortKey="teams" sort={sort} onToggle={sortBy} title={TEAMS_HELP} />
              </tr>
            </thead>
            <tbody>
              {sorted.map((p) => (
                <tr key={p.oid}>
                  <td className="grid__primary">
                    {p.name}
                    <div className="muted" style={{ fontSize: 12 }}>
                      {p.upn ?? 'has not signed in yet'}
                      {p.jobTitle ? ` · ${p.jobTitle}` : ''}
                      {p.accountEnabled === false ? ' · account disabled' : ''}
                    </div>
                  </td>
                  <td>
                    {p.departments.length ? (
                      p.departments.join(', ')
                    ) : outsideDepartments(p) ? (
                      <span className="pill pill--warn" title={NO_GROUP_HELP}>
                        Not in a department group
                      </span>
                    ) : (
                      <span className="muted" title="Admin: every department">—</span>
                    )}
                  </td>
                  {ROLES.map((x) => (
                    <td key={x.role} style={{ textAlign: 'center' }}>
                      <input type="checkbox" aria-label={`${p.name}: ${x.label}`} checked={p.roles.includes(x.role)} disabled={busy !== null || (x.role === 'Admin' && p.oid.toLowerCase() === me.oid.toLowerCase())} title={x.role === 'Admin' && p.oid.toLowerCase() === me.oid.toLowerCase() ? 'Another Admin has to change your Admin role' : x.help} onChange={() => toggle(p, x.role)} />
                    </td>
                  ))}
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {p.teams.connected ? <span className="pill pill--status-Published">Connected</span> : <span className="pill pill--status-Draft">Not connected</span>}{' '}
                    {p.teams.connected ? (
                      <button type="button" className="link-button" disabled={busy !== null} onClick={() => void testTeams(p)}>
                        Send test
                      </button>
                    ) : (
                      <button type="button" className="link-button" aria-label={`Connect ${p.name} to Teams`} disabled={busy !== null || p.accountEnabled === false} onClick={() => void connectTeams(p)}>
                        Connect
                      </button>
                    )}
                    {p.teams.note && (
                      <div className="muted" style={{ fontSize: 12, marginTop: 4, whiteSpace: 'normal', maxWidth: 320 }}>
                        {p.teams.note}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
